/**
 * Ported to TS from here: tailwindcss/src/util/flattenColorPalette
 */

type ColorPalette = Record<string, any>;
type FlattenedPalette = Record<string, string>;

/**
 * Constructs the color name based on the color key.
 * Appends the key to the color, unless the key is 'DEFAULT'.
 */
const formatColorName = (color: string, key: string): string => {
  const isDefault = key === 'DEFAULT';
  return isDefault ? color : `${color}-${key}`;
};

/**
 * Recursively flattens a nested color palette.
 *
 * It checks if the value is an object (i.e., nested), it recursively flattens the object
 * and formats the keys. Otherwise, it returns the key-value pair as is.
 */
const flattenPalette = (palette: ColorPalette): FlattenedPalette[] => {
  return Object.entries(palette).flatMap(([color, value]) => {
    const isNestedPalette = typeof value === 'object' && value !== null;

    if (isNestedPalette) {
      const nestedPalette = flattenColorPalette(value as ColorPalette);
      return Object.entries(nestedPalette).map(([nestedKey, nestedValue]) => {
        const colorName = formatColorName(color, nestedKey);
        return { [colorName]: nestedValue };
      });
    }

    // For non-object values, return the key-value pair as is
    return [{ [color]: value }];
  });
};

/**
 * Entry point function to flatten color palettes.
 *
 * This function takes a color palette object and returns a new object
 * with the nested color palettes flattened and the keys formatted.
 */
export const flattenColorPalette = (colors: ColorPalette | null | undefined): FlattenedPalette => {
  // Fallback to an empty object if colors is nullish
  const colorsToFlatten = colors ?? {};
  const flattenedColors = flattenPalette(colorsToFlatten);

  // Merge all entries into a single object
  return Object.assign({}, ...flattenedColors);
};
